import { Col, Form, Row } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import isinUpload, { IIsinUpload } from 'services/akcionar/emise/isinUpload';
import { IListIsin, getIsinList } from 'services/akcionar/emise/novyIsinSend';
import zadostZmenaApi, { IisinZmena } from 'services/akcionar/emise/zmenaISIN/zadostZmena';
import fileDownload from 'services/fileDownload';
import { prepareFileIsinApi } from 'services/prepareFile';

import { druhCpSelect } from 'data/codebooks/emise/udaje/druhCpSelect';

import nepovoleneZnakyValidation from 'utils/validations/nepovoleneZnakyValidation';

const { Item } = Form;

interface IRozcestnik {
    prevStep: () => void;
    nextStep: () => void;
    zadostID: number;
    setZruseniData: React.Dispatch<React.SetStateAction<IisinZmena | undefined>>;
    zruseniData?: IisinZmena;
}

const DuvodZruseni: React.FC<IRozcestnik> = ({ prevStep, nextStep, zadostID, setZruseniData, zruseniData }) => {
    const { profily } = useContext(Context);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);
    const [fileList, setFileList] = useState<IFileList[] | undefined>([]);
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<IIsinUpload[] | undefined>([]);
    const [listISIN, setListISIN] = useState<IListIsin[]>([]);
    const [zvolenyIsin, setZvolenyIsin] = useState<IListIsin>();

    useEffect(() => {
        setLoading(true);
        getIsinList().then(resp => {
            setListISIN(resp.isininfos ? resp.isininfos : []);
        });
        if (zadostID) {
            zadostZmenaApi('GET', zadostID, 'Zruseni', 'Zruseni').then(data => {
                setZruseniData(data);
                form.setFieldsValue(data);
            });
            isinUpload('get', zadostID ? zadostID : 0)
                .then(resp => {
                    setPrilozeneSoubory(resp.soubor);
                    setFileList(
                        resp.soubor &&
                            resp.soubor.map((e, i) => {
                                return {
                                    uid: String(i),
                                    oid: e.oid ? e.oid : 0,
                                    name: e.orignazev ? e.orignazev : 'bez nazvu',
                                    fileName: e.tempnazev ? e.tempnazev : 'a',
                                    type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                                    size: 0,
                                    status: 'done'
                                };
                            })
                    );
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uploadProps = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            data.event && setUploadLoading(true);
            if (data.fileList)
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                // oid: resp.id!,
                                uid: e.uid!,
                                name: e.name.normalize()!,
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : ''!,
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            if (data.fileList.length) {
                if (data.file.response) {
                    return isinUpload('post', zadostID ? zadostID : 0, {
                        tempnazev: data.file.response.tempnazev,
                        orignazev: data.file.name,
                        oid: data.file.response.oid
                    })
                        .then(resp => {
                            setPrilozeneSoubory(
                                data.fileList.map(e => {
                                    return {
                                        orignazev: e.name.normalize(),
                                        tempnazev: e.response ? e.response.tempnazev : e.fileName,
                                        uid: e.uid,
                                        oid: resp.id
                                    };
                                })
                            );
                            setFileList(
                                data.fileList
                                    .filter(f => f.status && f.status !== 'error')
                                    .map(e => {
                                        return {
                                            oid: resp.id,
                                            uid: e.uid,
                                            name: e.name.normalize(),
                                            fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                            type: e.type!,
                                            size: e.size!,
                                            status: e.status ? e.status : 'error'
                                        };
                                    })
                            );
                            form.validateFields(['identifikaceosoby']);
                        })

                        .finally(() => setUploadLoading(false));
                }
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const toDelete = oldState.filter(e => e.uid === file.uid);
                const newState = oldState.filter(e => e.uid !== file.uid);
                isinUpload('delete', zadostID ? zadostID : 0, undefined, toDelete[0].oid).then(() =>
                    setUploadLoading(false)
                );
                setFileList(newState);
                const newSoubory = prilozeneSoubory
                    ? [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName)
                    : [];
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFileIsinApi(zadostID, file.fileName ? file.fileName : '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false,
        buttonLoading: uploadLoading
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        const putData = {
            cval: value.cval,
            duvod: value.duvod,
            emailkont: profily!.email,
            telefon: profily!.telefon1,
            jmeno: profily!.jmeno,
            prijmeni: profily!.prijmeni
        };
        setLoading(true);
        setZruseniData(putData);
        zadostZmenaApi('PUT', zadostID!, 'Zruseni', 'Zruseni', putData)
            .then(() => nextStep())
            .finally(() => setLoading(false));
    };

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper onFinish={onFinish} outForm={form}>
                <SelectComponent
                    name="cval"
                    labelParagraph={'ISIN'}
                    data={(listISIN || []).map(e => {
                        return {
                            labelcz: e.cval,
                            value: e.cval
                        };
                    })}
                    defaultValue={zvolenyIsin?.cval}
                    isFormItem={true}
                    onChange={val => {
                        const data = listISIN.filter(e => e.cval === val)[0];
                        setZvolenyIsin(data);
                        form.setFieldsValue({
                            druhcp: data.druhcp
                        });
                    }}
                />
                {zvolenyIsin && (
                    <SelectComponent
                        name="druhcp"
                        labelParagraph="ISIN_EMISE_DRUH"
                        isFormItem={true}
                        disabled={true}
                        defaultValue={zvolenyIsin?.druhcp}
                        data={druhCpSelect}
                    />
                )}
                <Item
                    label={<Paragraph text={'ISIN_DUVOD_ZRUSENI_DUVODY'} strong={true} />}
                    name="duvod"
                    rules={[
                        {
                            required: true,
                            message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                        },
                        ...nepovoleneZnakyValidation()
                    ]}
                    initialValue={zruseniData?.duvod}
                >
                    <TextArea showCount={true} maxLength={500} defaultValue={zruseniData?.duvod} />
                </Item>
                <Form.Item
                    style={{
                        marginTop: 16,
                        marginBottom: 40
                    }}
                    name="identifikaceosoby"
                    validateTrigger="onSubmit"
                    label={<Paragraph text={'SOUBOR'} strong={true} />}
                    rules={[
                        {
                            required: true,
                            validator() {
                                try {
                                    if (fileList && fileList.length < 1)
                                        return Promise.reject(
                                            <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                        );
                                    return Promise.resolve();
                                } catch {
                                    return Promise.reject();
                                }
                            }
                        }
                    ]}
                >
                    <div
                        style={{
                            marginBottom: 22
                        }}
                    >
                        {/*   <Paragraph
                            style={{
                                display: 'block',
                                paddingBottom: 6
                            }}
                            text={'ISIN_ZRUSENI_SOUBOR'}
                        />*/}

                        <UploadComponent {...uploadProps} buttonId="DuvodZruseniUploadBtn" />
                    </div>
                </Form.Item>
                <Row className="button-row">
                    <Col>
                        <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} type="default" id="zpetBtn" />
                    </Col>
                    <Col>
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            onClick={() => form.submit()}
                            id="pokracovatBtn"
                            loading={uploadLoading}
                        />
                    </Col>
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default DuvodZruseni;
